import React, { Component } from 'react';
import styled, { css } from 'styled-components';
import ErrorMessage from '@zocdoc-frontend-common/components-errormessage';
import BaseTextbox from '@zocdoc-frontend-common/components-textbox';
import { Column, Row as BaseRow } from '@zocdoc-frontend-common/styles-grid';
import { sharpSansMedium } from '@zocdoc-frontend-common/styles-typography';
import { filterToValidAttributes } from '@zocdoc-frontend-common/utils-dom-property';
import { red70 } from '@zocdoc-mezzanine/foundations-colors';
import { labelExtraSmall } from '@zocdoc-mezzanine/foundations-typography';
import { coral, grey25, navy } from '@zocdoc/branding/colors';
import { BIRTH_YEAR, BIRTH_MONTH, BIRTH_DAY } from './constants';

/**
 * @param {Object} day
 * @param {string} focusOn
 * @param {string} invalidMessage
 * @param {Object} month
 * @param {Object} year
 * @param {boolean} shouldUseMezzanineColors
 * @return {JSX.Element}
 */
export class DateOfBirthPickerView extends Component {
    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.focusOn !== prevState.cachedFocusOn) {
            return {
                cachedFocusOn: nextProps.focusOn,
                setFocus: nextProps.focusOn,
            };
        }

        return {
            setFocus: undefined,
        };
    }

    state = {
        setFocus: undefined,
    };

    render() {
        const {
            day,
            invalidMessage,
            month,
            year,
            shouldUseMezzanineColors,
            ...props
        } = this.props;

        const { setFocus } = this.state;

        return (
            <Wrapper
                {...filterToValidAttributes(props)}
                $shouldUseMezzanineColors={shouldUseMezzanineColors}
            >
                <Row>
                    <Column size={8}>
                        <Textbox
                            className="exclude-from-playback"
                            data-date-is-invalid={invalidMessage}
                            data-test={BIRTH_MONTH}
                            data-uem-id="birth-month"
                            focusOn={setFocus === BIRTH_MONTH}
                            name={BIRTH_MONTH}
                            placeholder="MM"
                            type="tel"
                            $shouldUseMezzanineColors={shouldUseMezzanineColors}
                            {...month}
                        />
                    </Column>

                    <Column size={8}>
                        <Textbox
                            className="exclude-from-playback"
                            data-date-is-invalid={invalidMessage}
                            data-test={BIRTH_DAY}
                            data-uem-id="birth-day"
                            focusOn={setFocus === BIRTH_DAY}
                            name={BIRTH_DAY}
                            placeholder="DD"
                            type="tel"
                            $shouldUseMezzanineColors={shouldUseMezzanineColors}
                            {...day}
                        />
                    </Column>

                    <Column size={8}>
                        <Textbox
                            className="exclude-from-playback"
                            data-date-is-invalid={invalidMessage}
                            data-test={BIRTH_YEAR}
                            data-uem-id="birth-year"
                            focusOn={setFocus === BIRTH_YEAR}
                            name={BIRTH_YEAR}
                            placeholder="YYYY"
                            type="tel"
                            $shouldUseMezzanineColors={shouldUseMezzanineColors}
                            {...year}
                        />
                    </Column>
                </Row>
                {this.renderInvalidErrorMessage()}
            </Wrapper>
        );
    }

    renderInvalidErrorMessage() {
        if (this.props.invalidMessage) {
            return (
                <Row>
                    <ErrorMessage
                        data-test="date-of-birth-error-message"
                        $shouldUseMezzanineColors={
                            this.props.shouldUseMezzanineColors
                        }
                    >
                        {this.props.invalidMessage}
                    </ErrorMessage>
                </Row>
            );
        }
    }
}

// using first-child instead of ${Textbox} because styling against Textbox
// passes the styles down a level directly to the input, rather than the wrapper div
const Row = styled(BaseRow)`
    ${Column}:not(:first-child) > * {
        margin-left: -1px;
    }
`;

const Textbox = styled(BaseTextbox).attrs((p) => ({
    invalid: p['data-date-is-invalid'],
}))`
    ${sharpSansMedium(15, 24, 1)}
    border-color: ${(p) =>
        p.invalid ? (p.$shouldUseMezzanineColors ? red70 : coral) : grey25};
    color: ${navy};
    position: relative;

    &:focus {
        border-color: ${(p) =>
            p.invalid ? (p.$shouldUseMezzanineColors ? red70 : coral) : navy};
        z-index: 1;
    }

    &[type='number']::-webkit-inner-spin-button,
    &[type='number']::-webkit-outer-spin-button {
        appearance: none;
        margin: 0;
    }
`;

const Wrapper = styled.div`
    ${(p) =>
        p.$shouldUseMezzanineColors &&
        css`
            [data-test='date-of-birth-error-message'] {
                color: ${red70};
                ${labelExtraSmall};
            }
        `}
`;
